const CPF_LENGTH = 11;
const CNPJ_LENGTH = 14;
const CNPJ_BASE_LENGTH = 12;
const CNPJ_ROOT_LENGTH = 8;

const cpfWeightSequence = [10, 9, 8, 7, 6, 5, 4, 3, 2];
const cnpjWeightSequence = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

const alphaNumericPattern = /^[0-9A-Z]+$/;
const toUpperAlphanumeric = value =>
  (value || "").toString().toUpperCase().replace(/[^0-9A-Z]/g, "");

const toDigits = value => (value || "").toString().replace(/\D/g, "");

const cpfDigit = value => {
  const weights = value.length === 9 ? cpfWeightSequence : [11, ...cpfWeightSequence];
  let sum = 0;
  for (let index = 0; index < value.length; index++) {
    sum += Number(value[index]) * weights[index];
  }
  const rest = sum % 11;
  return rest < 2 ? 0 : 11 - rest;
};

const asciiToDvValue = character => character.charCodeAt(0) - 48;

const cnpjDigit = value => {
  const weights = value.length === CNPJ_BASE_LENGTH ? cnpjWeightSequence : [6, ...cnpjWeightSequence];
  let sum = 0;

  for (let index = 0; index < value.length; index++) {
    sum += asciiToDvValue(value[index]) * weights[index];
  }

  const rest = sum % 11;
  return rest < 2 ? 0 : 11 - rest;
};

export const normalizeCpf = value => toDigits(value).slice(0, CPF_LENGTH);

export const normalizeCnpj = value => toUpperAlphanumeric(value).slice(0, CNPJ_LENGTH);

export const detectDocumentType = value => {
  const cleanAlphaNumeric = toUpperAlphanumeric(value);
  const cleanDigits = toDigits(value);
  const hasLetters = /[A-Z]/.test(cleanAlphaNumeric);

  if (cleanDigits.length === CPF_LENGTH && cleanDigits.length === cleanAlphaNumeric.length) {
    return "cpf";
  }

  if (hasLetters) {
    return "cnpj";
  }

  if (cleanAlphaNumeric.length >= CPF_LENGTH && cleanAlphaNumeric.length <= CNPJ_LENGTH) {
    return cleanAlphaNumeric.length === CPF_LENGTH ? "cpf" : "cnpj";
  }

  if (cleanDigits.length === CPF_LENGTH) {
    return "cpf";
  }

  if (cleanAlphaNumeric.length === CNPJ_LENGTH) {
    return "cnpj";
  }

  return cleanAlphaNumeric.length > CPF_LENGTH ? "cnpj" : "cpf";
};

export const formatCpf = value => {
  const clean = normalizeCpf(value);
  if (clean.length <= 3) return clean;
  if (clean.length <= 6) return `${clean.slice(0, 3)}.${clean.slice(3)}`;
  if (clean.length <= 9) return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6)}`;
  return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6, 9)}-${clean.slice(9)}`;
};

export const formatCnpj = value => {
  const clean = normalizeCnpj(value);
  if (clean.length <= CNPJ_ROOT_LENGTH) {
    return clean;
  }

  const root = clean.slice(0, CNPJ_ROOT_LENGTH);
  const branch = clean.slice(CNPJ_ROOT_LENGTH, CNPJ_BASE_LENGTH);
  const digits = clean.slice(CNPJ_BASE_LENGTH);
  return `${root}/${branch}-${digits}`;
};

export const formatDocument = value => {
  const type = detectDocumentType(value);
  return type === "cpf" ? formatCpf(value) : formatCnpj(value);
};

export const documentMask = value => {
  const type = detectDocumentType(value);
  return type === "cpf" ? ["###.###.###-##"] : ["##.###.###/####-##"];
};

export const isValidCpf = value => {
  const cpf = normalizeCpf(value);

  if (cpf.length !== CPF_LENGTH || /^(\d)\1{10}$/.test(cpf)) {
    return false;
  }

  const firstDigit = cpfDigit(cpf.slice(0, 9));
  const secondDigit = cpfDigit(cpf.slice(0, 9) + firstDigit);

  return cpf === `${cpf.slice(0, 9)}${firstDigit}${secondDigit}`;
};

export const isValidCnpj = value => {
  const cnpj = normalizeCnpj(value);

  if (
    cnpj.length !== CNPJ_LENGTH ||
    !alphaNumericPattern.test(cnpj) ||
    !/^\d{2}$/.test(cnpj.slice(-2))
  ) {
    return false;
  }

  const base = cnpj.slice(0, CNPJ_BASE_LENGTH);
  const firstDigit = cnpjDigit(base);
  const secondDigit = cnpjDigit(`${base}${firstDigit}`);

  return cnpj === `${base}${firstDigit}${secondDigit}`;
};

export const isValidDocument = value => {
  const cleanDigits = toDigits(value);
  const cleanAlphaNumeric = toUpperAlphanumeric(value);

  if (cleanDigits.length === CPF_LENGTH && cleanDigits.length === cleanAlphaNumeric.length) {
    return isValidCpf(cleanDigits);
  }

  if (cleanAlphaNumeric.length === CPF_LENGTH) {
    return isValidCpf(cleanAlphaNumeric);
  }

  if (cleanAlphaNumeric.length === CNPJ_LENGTH) {
    return isValidCnpj(cleanAlphaNumeric);
  }

  return false;
};

export const documentTypeMatches = (type, value) => {
  if (type === "cpf") return isValidCpf(value);
  if (type === "cnpj") return isValidCnpj(value);
  return isValidDocument(value);
};
